import React, { useContext } from 'react';
import { StudiestedApiContext } from '../../api/StudiestedApiContext';
import { useIntl } from 'react-intl';
import translations from './agreementduplicatewarning.i18n';
import { useLoader } from '../../api/useLoader';
import { praksistyperToKoder } from './agreementutils';
import {
  MinimalPraksisstedDto,
  PraksisstedavtaleDto,
} from '../../api/generated/studiested-generated-api';
import * as css from './agreementduplicatewarning.module.scss';
import * as mainCss from './practiceplaceagreement.module.scss';
import { formatLocaleNo } from '../../date.utils';
import {
  AgreementState,
  PraksisstedForAvtale,
  useAgreementContext,
} from './index';
import { ConfirmationDialog } from '../../components/dialog';
import { useFormatMessageFor } from '../../i18n/translation';
import PanelHeader from '../../components/panelheader/panelHeader';
import { formatToSummaryString } from '../../util/stringutils';
import { getLanguageFromLocale } from '../../i18n/LanguageStore';
import { Link, useParams } from 'react-router-dom';
import Spinner from '../../components/spinner/spinner';
import {
  ListItem,
  OrderedList,
  UnorderedList,
} from '../../components/list/list';

const useGetOverlappingAvtalestederFor = (): ((
  overlappendeAvtale: PraksisstedavtaleDto,
) => MinimalPraksisstedDto[]) => {
  const { praksissteder } = useAgreementContext();

  if (!praksissteder) return () => [];

  return (overlappendeAvtale: PraksisstedavtaleDto) =>
    Object.values(overlappendeAvtale.avtalesteder).map((avtalested) => {
      const { avtalestedforelder } = avtalested;
      const { id, navn } = avtalestedforelder!;

      return { id, navn };
    });
};

interface AgreementDuplicateWarningProps {
  overlappendeAvtaler: PraksisstedavtaleDto[];
}

const AgreementDuplicateWarning: React.FC<
  React.PropsWithChildren<AgreementDuplicateWarningProps>
> = ({ overlappendeAvtaler }) => {
  const getOverlappingAvtalestederFor = useGetOverlappingAvtalestederFor();
  const { formatMessage, locale } = useIntl();
  const labels = useFormatMessageFor(translations);

  const getPeriodeMessageFor = ({
    avtalestart,
    avtaleslutt,
  }: PraksisstedavtaleDto) =>
    formatMessage(
      { id: translations.periodeFormat.id },
      {
        avtalestart: avtalestart ? formatLocaleNo(avtalestart) : '',
        avtaleslutt: avtaleslutt
          ? formatLocaleNo(avtaleslutt)
          : labels.tilSagtOpp,
      },
    );

  const getPraksistypeMessageFor = ({ praksistyper }: PraksisstedavtaleDto) =>
    formatToSummaryString(
      praksistyperToKoder(praksistyper),
      getLanguageFromLocale(locale),
    );

  const getAvtalestederMessageFor = (avtale: PraksisstedavtaleDto) =>
    formatToSummaryString(
      getOverlappingAvtalestederFor(avtale).map(
        (avtalested) => avtalested.navn,
      ),
      getLanguageFromLocale(locale),
    );

  return (
    <div className={css.duplicateContent}>
      <OrderedList>
        {overlappendeAvtaler.map((avtale) => (
          <ListItem
            key={`overlappende-avtale-${avtale.avtaleId}`}
            data-cy={'overlap-row'}
          >
            <Link to={avtale.avtaleId}>{avtale.avtalenavn}</Link>
            <UnorderedList>
              <ListItem>
                <strong>{labels.periodeLabel}</strong>
                {getPeriodeMessageFor(avtale)}
              </ListItem>
              <ListItem>
                <strong>{labels.praksistyperLabel}</strong>
                {getPraksistypeMessageFor(avtale)}
              </ListItem>
              <ListItem>
                <strong>{labels.avtalestederLabel}</strong>
                {getAvtalestederMessageFor(avtale)}
              </ListItem>
            </UnorderedList>
          </ListItem>
        ))}
      </OrderedList>
    </div>
  );
};

const mapAvtalestederToIds = (
  avtalesteder: PraksisstedForAvtale[] | undefined,
): string[] => avtalesteder?.map((praksissted) => praksissted.id) ?? [];

type OverlapRequestParams = {
  avtaleId: string | undefined;
  studiestedIdEierAvAvtale: string | undefined;
  praksistypekoder: string[];
  startdato: string | undefined;
  sluttdato: string | undefined;
  avtalestedIder: string[];
};

export const useOverlapRequest = (): ((
  params: OverlapRequestParams,
) => Promise<PraksisstedavtaleDto[]>) => {
  const { praksisstedApi } = useContext(StudiestedApiContext);

  return (params: OverlapRequestParams) => {
    const {
      avtaleId,
      studiestedIdEierAvAvtale,
      praksistypekoder,
      startdato,
      sluttdato,
      avtalestedIder,
    } = params;

    if (
      !startdato ||
      !studiestedIdEierAvAvtale ||
      !praksistypekoder.length ||
      !avtalestedIder.length
    ) {
      return Promise.resolve([]);
    }

    return praksisstedApi
      .avtalerPraksisSokOverlappendeavtalerPost({
        overlappendePraksisstedavtalesokRequestDto: {
          studiestedIdEierAvAvtale,
          praksistypekoder,
          avtalestart: startdato,
          avtaleslutt: sluttdato || undefined,
          avtalestedIder,
        },
      })
      .then((overlappendeAvtaler) =>
        overlappendeAvtaler.overlappendeAvtaler.filter(
          (duplisertAvtale) => duplisertAvtale.avtaleId !== avtaleId,
        ),
      );
  };
};

const useTranslations = (
  overlappendeAvtaler: PraksisstedavtaleDto[] | undefined,
) =>
  useFormatMessageFor({
    ...translations,
    modalTitle: {
      ...translations.modalTitle,
      values: { antall: `${overlappendeAvtaler?.length}` },
    },
  });

interface AgreementDuplicateWarningConfirmationProps {
  overlappendeAvtaler: PraksisstedavtaleDto[];
  onClose: (confirm: boolean) => void;
}

export const AgreementDuplicateWarningConfirmation: React.FC<
  React.PropsWithChildren<AgreementDuplicateWarningConfirmationProps>
> = ({ overlappendeAvtaler, onClose }) => {
  const labels = useTranslations(overlappendeAvtaler);

  return (
    <ConfirmationDialog
      title={labels.modalTitle}
      subtitle={labels.subtitle}
      submitLabel={labels.submitLabel}
      onClose={onClose}
      isOpen
    >
      <AgreementDuplicateWarning overlappendeAvtaler={overlappendeAvtaler} />
    </ConfirmationDialog>
  );
};

const SPINNER_HEIGHT = '3em';

interface AgreementDuplicateWarningSectionProps {
  initAgreement: AgreementState;
}

const AgreementDuplicateWarningSectionWrapper: React.FC<
  React.PropsWithChildren<AgreementDuplicateWarningSectionProps>
> = ({ initAgreement }) => {
  const { avtaleId } = useParams<{ avtaleId?: string }>();

  if (initAgreement.avtaleId !== avtaleId)
    return <Spinner height={SPINNER_HEIGHT} />;

  return <AgreementDuplicateWarningSection initAgreement={initAgreement} />;
};

const AgreementDuplicateWarningSection: React.FC<
  React.PropsWithChildren<AgreementDuplicateWarningSectionProps>
> = ({ initAgreement }) => {
  const overlapRequest = useOverlapRequest();

  const {
    avtaleId,
    startdato,
    sluttdato,
    studiested: initAvtaleeier,
    praksissteder: initPraksissteder,
    praksistyper: initPraksisyper,
  } = initAgreement;

  const { data: overlappendeAvtaler, loading } = useLoader(
    () =>
      overlapRequest({
        avtaleId,
        studiestedIdEierAvAvtale: initAvtaleeier?.id,
        startdato,
        sluttdato,
        praksistypekoder: praksistyperToKoder(initPraksisyper),
        avtalestedIder: mapAvtalestederToIds(initPraksissteder),
      }),
    [initAgreement],
  );

  const labels = useTranslations(overlappendeAvtaler);

  if (loading) return <Spinner height={SPINNER_HEIGHT} />;

  if (!overlappendeAvtaler || overlappendeAvtaler.length === 0) return <></>;

  return (
    <PanelHeader
      title={labels.fantOverlappendeAvtaleTitle}
      subtitle={labels.subtitle}
      childrenClassName={mainCss.headerChildren}
      collapsible
    >
      <AgreementDuplicateWarning overlappendeAvtaler={overlappendeAvtaler} />
    </PanelHeader>
  );
};

export default AgreementDuplicateWarningSectionWrapper;
